// Routes Configuration
// All page imports and route definitions in one place
// URL Structure: /parent-category/child-page
// Legacy URLs redirect to new nested structure for SEO

import { lazy } from "react";
import SEORedirect from "./components/shared/SEORedirect";

// Core Pages - New simplified structure
import AboutMePage from "./pages/AboutMePage";
import YogaClassesPage from "./pages/YogaClassesPage";
import TreatmentsPage from "./pages/TreatmentsPage";
import WomensCirclesPage from "./pages/WomensCirclesPage";
import RetreatsPage from "./pages/RetreatsPage";
import YogaRetreatsUK from "./pages/YogaRetreatsUK";
import ContactPage from "./pages/ContactPage";

// SEO Supporting Pages (keep live for SEO)
import WhatToExpectYogaRetreat from "./pages/WhatToExpectYogaRetreat";
import YogaRetreatPackingList from "./pages/YogaRetreatPackingList";
import BenefitsOfYogaRetreats from "./pages/BenefitsOfYogaRetreats";
import SoundHealingRetreatsUK from "./pages/SoundHealingRetreatsUK";
import YogaRetreatForBeginners from "./pages/YogaRetreatForBeginners";
import WeekendYogaRetreatsUK from "./pages/WeekendYogaRetreatsUK";
import ServicesPage from "./pages/ServicesPage";
import FAQPage from "./pages/FAQPage";
import AreasPage from "./pages/AreasPage";
import BeginnerYogaPage from "./pages/BeginnerYogaPage";
import PrivateYogaPage from "./pages/PrivateYogaPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import CookiePolicyPage from "./pages/CookiePolicyPage";
import RemoteSessionsPage from "./pages/RemoteSessionsPage";

// Service Pages
import HathaYogaFrome from "./pages/services/HathaYogaFrome";
import YinYogaFrome from "./pages/services/YinYogaFrome";
import WomensCirclesFrome from "./pages/services/WomensCirclesFrome";
import HolisticTreatmentsFrome from "./pages/services/HolisticTreatmentsFrome";
import AerialYogaFrome from "./pages/services/AerialYogaFrome";
import ReikiHealingFrome from "./pages/services/ReikiHealingFrome";
import ChakraHealingFrome from "./pages/services/ChakraHealingFrome";
import SwedishMassageFrome from "./pages/services/SwedishMassageFrome";
import ChildrenFamilyYogaFrome from "./pages/services/ChildrenFamilyYogaFrome";
import SENYogaFrome from "./pages/services/SENYogaFrome";
import MeditationFrome from "./pages/services/MeditationFrome";

// Location Pages
import YogaBath from "./pages/locations/YogaBath";
import YogaTrowbridge from "./pages/locations/YogaTrowbridge";
import YogaWarminster from "./pages/locations/YogaWarminster";
import YogaSheptonMallet from "./pages/locations/YogaSheptonMallet";
import YogaWells from "./pages/locations/YogaWells";
import YogaRadstock from "./pages/locations/YogaRadstock";
import YogaWestbury from "./pages/locations/YogaWestbury";
import YogaMells from "./pages/locations/YogaMells";
import YogaNunney from "./pages/locations/YogaNunney";
import YogaBeckington from "./pages/locations/YogaBeckington";
import YogaMidsomerNorton from "./pages/locations/YogaMidsomerNorton";
import YogaBradfordOnAvon from "./pages/locations/YogaBradfordOnAvon";

// County Hub Pages
import YogaSomerset from "./pages/YogaSomerset";
import YogaWiltshire from "./pages/YogaWiltshire";

// Service + Location Combo Pages
import HathaYogaBath from "./pages/services/locations/HathaYogaBath";
import YinYogaBath from "./pages/services/locations/YinYogaBath";
import PrivateYogaBath from "./pages/services/locations/PrivateYogaBath";
import HathaYogaTrowbridge from "./pages/services/locations/HathaYogaTrowbridge";
import YinYogaTrowbridge from "./pages/services/locations/YinYogaTrowbridge";
import PrivateYogaTrowbridge from "./pages/services/locations/PrivateYogaTrowbridge";
import HathaYogaWarminster from "./pages/services/locations/HathaYogaWarminster";
import YinYogaWarminster from "./pages/services/locations/YinYogaWarminster";
import PrivateYogaWarminster from "./pages/services/locations/PrivateYogaWarminster";
import HathaYogaWells from "./pages/services/locations/HathaYogaWells";
import YinYogaWells from "./pages/services/locations/YinYogaWells";
import PrivateYogaWells from "./pages/services/locations/PrivateYogaWells";
import HathaYogaRadstock from "./pages/services/locations/HathaYogaRadstock";
import YinYogaRadstock from "./pages/services/locations/YinYogaRadstock";
import PrivateYogaRadstock from "./pages/services/locations/PrivateYogaRadstock";
import HathaYogaWestbury from "./pages/services/locations/HathaYogaWestbury";
import YinYogaWestbury from "./pages/services/locations/YinYogaWestbury";
import PrivateYogaWestbury from "./pages/services/locations/PrivateYogaWestbury";
import HathaYogaSheptonMallet from "./pages/services/locations/HathaYogaSheptonMallet";
import YinYogaSheptonMallet from "./pages/services/locations/YinYogaSheptonMallet";
import PrivateYogaSheptonMallet from "./pages/services/locations/PrivateYogaSheptonMallet";

// Helper to create redirect component
const createRedirect = (to) => () => <SEORedirect to={to} />;

// Route definitions organized by category
// NEW URL STRUCTURE: /parent/child for proper hierarchy
export const routes = {
  // Core Pages (main navigation)
  core: [
    { path: "/about", element: AboutMePage },
    { path: "/yoga-classes", element: YogaClassesPage },
    { path: "/treatments", element: TreatmentsPage },
    { path: "/womens-circles", element: WomensCirclesPage },
    { path: "/retreats", element: RetreatsPage }, // Hub page for retreats
    { path: "/contact", element: ContactPage },
    { path: "/services", element: ServicesPage },
    { path: "/faq", element: FAQPage },
    { path: "/areas", element: AreasPage },
  ],

  // Core page redirects (consolidate duplicate intents)
  coreRedirects: [
    { path: "/about-me", element: createRedirect("/about") }, // Alias redirects to canonical
  ],

  // Yoga Classes Children - NEW nested URLs
  yogaClassesChildren: [
    { path: "/yoga-classes/hatha-yoga", element: HathaYogaFrome },
    { path: "/yoga-classes/yin-yoga", element: YinYogaFrome },
    { path: "/yoga-classes/aerial-yoga", element: AerialYogaFrome },
    { path: "/yoga-classes/private-yoga", element: PrivateYogaPage },
    { path: "/yoga-classes/beginner-yoga", element: BeginnerYogaPage },
    { path: "/yoga-classes/children-family-yoga", element: ChildrenFamilyYogaFrome },
    { path: "/yoga-classes/sen-yoga", element: SENYogaFrome },
    { path: "/yoga-classes/meditation", element: MeditationFrome },
  ],

  // Yoga Classes Legacy URL Redirects (old flat URLs -> new nested structure)
  yogaClassesRedirects: [
    { path: "/yoga-classes/private-yoga-sessions", element: createRedirect("/yoga-classes/private-yoga") },
    { path: "/private-yoga-sessions-frome", element: createRedirect("/yoga-classes/private-yoga") },
    { path: "/private-yoga-sessions", element: createRedirect("/yoga-classes/private-yoga") },
    { path: "/private-yoga-frome", element: createRedirect("/yoga-classes/private-yoga") },
    { path: "/hatha-yoga-frome", element: createRedirect("/yoga-classes/hatha-yoga") },
    { path: "/yin-yoga-frome", element: createRedirect("/yoga-classes/yin-yoga") },
    { path: "/aerial-yoga-frome", element: createRedirect("/yoga-classes/aerial-yoga") },
    { path: "/beginner-yoga", element: createRedirect("/yoga-classes/beginner-yoga") },
    { path: "/children-family-yoga-frome", element: createRedirect("/yoga-classes/children-family-yoga") },
    { path: "/sen-yoga-frome", element: createRedirect("/yoga-classes/sen-yoga") },
    { path: "/meditation-frome", element: createRedirect("/yoga-classes/meditation") },
    // Legacy "/offerings/*" pattern
    { path: "/offerings/aerial-yoga", element: createRedirect("/yoga-classes/aerial-yoga") },
    { path: "/offerings/hatha-yoga", element: createRedirect("/yoga-classes/hatha-yoga") },
    { path: "/offerings/yin-yoga", element: createRedirect("/yoga-classes/yin-yoga") },
    { path: "/offerings/private-yoga", element: createRedirect("/yoga-classes/private-yoga") },
    { path: "/offerings/meditation", element: createRedirect("/yoga-classes/meditation") },
  ],

  // Treatments Children - NEW nested URLs
  treatmentsChildren: [
    { path: "/treatments/reiki-healing", element: ReikiHealingFrome },
    { path: "/treatments/chakra-healing", element: ChakraHealingFrome },
    { path: "/treatments/swedish-massage", element: SwedishMassageFrome },
    { path: "/treatments/holistic-treatments", element: HolisticTreatmentsFrome },
  ],

  // Treatments Legacy URL Redirects (old flat URLs -> new nested structure)
  treatmentsRedirects: [
    { path: "/reiki-healing-frome", element: createRedirect("/treatments/reiki-healing") },
    { path: "/chakra-healing-frome", element: createRedirect("/treatments/chakra-healing") },
    { path: "/swedish-massage-frome", element: createRedirect("/treatments/swedish-massage") },
    { path: "/holistic-treatments-frome", element: createRedirect("/treatments/holistic-treatments") },
    // Legacy "/offerings/*" pattern
    { path: "/offerings/reiki-healing", element: createRedirect("/treatments/reiki-healing") },
    { path: "/offerings/chakra-healing", element: createRedirect("/treatments/chakra-healing") },
    { path: "/offerings/swedish-massage", element: createRedirect("/treatments/swedish-massage") },
    { path: "/offerings/holistic-treatments", element: createRedirect("/treatments/holistic-treatments") },
  ],

  // Women's Circles Children - NEW nested URLs
  womensCirclesChildren: [
    { path: "/womens-circles/frome", element: WomensCirclesFrome },
  ],

  // Women's Circles Legacy URL Redirects (old flat URLs -> new nested structure)
  womensCirclesRedirects: [
    { path: "/womens-circles-frome", element: createRedirect("/womens-circles/frome") },
    { path: "/offerings/womens-circles", element: createRedirect("/womens-circles") },
  ],

  // Retreats Children - NEW nested URLs
  retreatsChildren: [
    { path: "/retreats/yoga-retreats-uk", element: YogaRetreatsUK },
    { path: "/retreats/what-to-expect", element: WhatToExpectYogaRetreat },
    { path: "/retreats/packing-list", element: YogaRetreatPackingList },
    { path: "/retreats/benefits", element: BenefitsOfYogaRetreats },
    { path: "/retreats/sound-healing", element: SoundHealingRetreatsUK },
    { path: "/retreats/beginners", element: YogaRetreatForBeginners },
    { path: "/retreats/weekend", element: WeekendYogaRetreatsUK },
  ],

  // Retreats Legacy URL Redirects (old flat URLs -> new nested structure)
  retreatsRedirects: [
    { path: "/yoga-retreats-uk", element: createRedirect("/retreats/yoga-retreats-uk") },
    { path: "/yoga-retreat-uk", element: createRedirect("/retreats/yoga-retreats-uk") },
    { path: "/retreats/uk", element: createRedirect("/retreats/yoga-retreats-uk") },
    { path: "/yoga-retreats-somerset", element: createRedirect("/retreats") },
    { path: "/benefits-of-yoga-retreats", element: createRedirect("/retreats/benefits") },
    { path: "/yoga-retreat-packing-list", element: createRedirect("/retreats/packing-list") },
    { path: "/what-to-expect-yoga-retreat", element: createRedirect("/retreats/what-to-expect") },
    { path: "/sound-healing-retreats-uk", element: createRedirect("/retreats/sound-healing") },
    { path: "/yoga-retreat-for-beginners", element: createRedirect("/retreats/beginners") },
    { path: "/weekend-yoga-retreats-uk", element: createRedirect("/retreats/weekend") },
  ],

  // Misc Legacy URL Redirects (top-level "offerings" hub etc.)
  miscRedirects: [
    { path: "/offerings", element: createRedirect("/services") },
    { path: "/offerings/children-family-yoga", element: createRedirect("/yoga-classes/children-family-yoga") },
    { path: "/offerings/chakras", element: createRedirect("/treatments/chakra-healing") },
    { path: "/offerings/chakras/sacral-chakra", element: createRedirect("/treatments/chakra-healing") },
    { path: "/offerings/chakras/root-chakra", element: createRedirect("/treatments/chakra-healing") },
    { path: "/offerings/chakras/solar-plexus-chakra", element: createRedirect("/treatments/chakra-healing") },
    { path: "/offerings/chakras/heart-chakra", element: createRedirect("/treatments/chakra-healing") },
    { path: "/offerings/chakras/throat-chakra", element: createRedirect("/treatments/chakra-healing") },
    { path: "/offerings/chakras/third-eye-chakra", element: createRedirect("/treatments/chakra-healing") },
    { path: "/offerings/chakras/crown-chakra", element: createRedirect("/treatments/chakra-healing") },
    { path: "/private-yoga", element: createRedirect("/yoga-classes/private-yoga") },
    { path: "/affordable-yoga-retreats-uk", element: createRedirect("/retreats/yoga-retreats-uk") },
  ],

  // Location pages (under /areas)
  locations: [
    { path: "/areas/yoga-bath", element: YogaBath },
    { path: "/areas/yoga-trowbridge", element: YogaTrowbridge },
    { path: "/areas/yoga-warminster", element: YogaWarminster },
    { path: "/areas/yoga-shepton-mallet", element: YogaSheptonMallet },
    { path: "/areas/yoga-wells", element: YogaWells },
    { path: "/areas/yoga-radstock", element: YogaRadstock },
    { path: "/areas/yoga-westbury", element: YogaWestbury },
    { path: "/areas/yoga-mells", element: YogaMells },
    { path: "/areas/yoga-nunney", element: YogaNunney },
    { path: "/areas/yoga-beckington", element: YogaBeckington },
    { path: "/areas/yoga-midsomer-norton", element: YogaMidsomerNorton },
    { path: "/areas/yoga-bradford-on-avon", element: YogaBradfordOnAvon },
    { path: "/areas/yoga-somerset", element: YogaSomerset },
    { path: "/areas/yoga-wiltshire", element: YogaWiltshire },
  ],

  // Location Legacy URL Redirects (without "yoga-" prefix -> with "yoga-" prefix)
  locationsRedirects: [
    { path: "/areas/bath", element: createRedirect("/areas/yoga-bath") },
    { path: "/areas/trowbridge", element: createRedirect("/areas/yoga-trowbridge") },
    { path: "/areas/warminster", element: createRedirect("/areas/yoga-warminster") },
    { path: "/areas/shepton-mallet", element: createRedirect("/areas/yoga-shepton-mallet") },
    { path: "/areas/wells", element: createRedirect("/areas/yoga-wells") },
    { path: "/areas/radstock", element: createRedirect("/areas/yoga-radstock") },
    { path: "/areas/westbury", element: createRedirect("/areas/yoga-westbury") },
    { path: "/areas/mells", element: createRedirect("/areas/yoga-mells") },
    { path: "/areas/nunney", element: createRedirect("/areas/yoga-nunney") },
    { path: "/areas/beckington", element: createRedirect("/areas/yoga-beckington") },
    { path: "/areas/midsomer-norton", element: createRedirect("/areas/yoga-midsomer-norton") },
    { path: "/areas/bradford-on-avon", element: createRedirect("/areas/yoga-bradford-on-avon") },
    { path: "/yoga-somerset", element: createRedirect("/areas/yoga-somerset") },
    { path: "/yoga-wiltshire", element: createRedirect("/areas/yoga-wiltshire") },
  ],

  // Service + Location combo pages (NEW nested structure)
  serviceLocations: [
    // Bath
    { path: "/areas/yoga-bath/hatha-yoga", element: HathaYogaBath },
    { path: "/areas/yoga-bath/yin-yoga", element: YinYogaBath },
    { path: "/areas/yoga-bath/private-yoga", element: PrivateYogaBath },
    // Trowbridge
    { path: "/areas/yoga-trowbridge/hatha-yoga", element: HathaYogaTrowbridge },
    { path: "/areas/yoga-trowbridge/yin-yoga", element: YinYogaTrowbridge },
    { path: "/areas/yoga-trowbridge/private-yoga", element: PrivateYogaTrowbridge },
    // Warminster
    { path: "/areas/yoga-warminster/hatha-yoga", element: HathaYogaWarminster },
    { path: "/areas/yoga-warminster/yin-yoga", element: YinYogaWarminster },
    { path: "/areas/yoga-warminster/private-yoga", element: PrivateYogaWarminster },
    // Wells
    { path: "/areas/yoga-wells/hatha-yoga", element: HathaYogaWells },
    { path: "/areas/yoga-wells/yin-yoga", element: YinYogaWells },
    { path: "/areas/yoga-wells/private-yoga", element: PrivateYogaWells },
    // Radstock
    { path: "/areas/yoga-radstock/hatha-yoga", element: HathaYogaRadstock },
    { path: "/areas/yoga-radstock/yin-yoga", element: YinYogaRadstock },
    { path: "/areas/yoga-radstock/private-yoga", element: PrivateYogaRadstock },
    // Westbury
    { path: "/areas/yoga-westbury/hatha-yoga", element: HathaYogaWestbury },
    { path: "/areas/yoga-westbury/yin-yoga", element: YinYogaWestbury },
    { path: "/areas/yoga-westbury/private-yoga", element: PrivateYogaWestbury },
    // Shepton Mallet
    { path: "/areas/yoga-shepton-mallet/hatha-yoga", element: HathaYogaSheptonMallet },
    { path: "/areas/yoga-shepton-mallet/yin-yoga", element: YinYogaSheptonMallet },
    { path: "/areas/yoga-shepton-mallet/private-yoga", element: PrivateYogaSheptonMallet },
  ],

  // Service + Location Legacy URL Redirects
  serviceLocationsRedirects: [
    // Bath
    { path: "/hatha-yoga-bath", element: createRedirect("/areas/yoga-bath/hatha-yoga") },
    { path: "/yin-yoga-bath", element: createRedirect("/areas/yoga-bath/yin-yoga") },
    { path: "/private-yoga-bath", element: createRedirect("/areas/yoga-bath/private-yoga") },
    // Trowbridge
    { path: "/hatha-yoga-trowbridge", element: createRedirect("/areas/yoga-trowbridge/hatha-yoga") },
    { path: "/yin-yoga-trowbridge", element: createRedirect("/areas/yoga-trowbridge/yin-yoga") },
    { path: "/private-yoga-trowbridge", element: createRedirect("/areas/yoga-trowbridge/private-yoga") },
    // Warminster
    { path: "/hatha-yoga-warminster", element: createRedirect("/areas/yoga-warminster/hatha-yoga") },
    { path: "/yin-yoga-warminster", element: createRedirect("/areas/yoga-warminster/yin-yoga") },
    { path: "/private-yoga-warminster", element: createRedirect("/areas/yoga-warminster/private-yoga") },
    // Wells
    { path: "/hatha-yoga-wells", element: createRedirect("/areas/yoga-wells/hatha-yoga") },
    { path: "/yin-yoga-wells", element: createRedirect("/areas/yoga-wells/yin-yoga") },
    { path: "/private-yoga-wells", element: createRedirect("/areas/yoga-wells/private-yoga") },
    // Radstock
    { path: "/hatha-yoga-radstock", element: createRedirect("/areas/yoga-radstock/hatha-yoga") },
    { path: "/yin-yoga-radstock", element: createRedirect("/areas/yoga-radstock/yin-yoga") },
    { path: "/private-yoga-radstock", element: createRedirect("/areas/yoga-radstock/private-yoga") },
    // Westbury
    { path: "/hatha-yoga-westbury", element: createRedirect("/areas/yoga-westbury/hatha-yoga") },
    { path: "/yin-yoga-westbury", element: createRedirect("/areas/yoga-westbury/yin-yoga") },
    { path: "/private-yoga-westbury", element: createRedirect("/areas/yoga-westbury/private-yoga") },
    // Shepton Mallet
    { path: "/hatha-yoga-shepton-mallet", element: createRedirect("/areas/yoga-shepton-mallet/hatha-yoga") },
    { path: "/yin-yoga-shepton-mallet", element: createRedirect("/areas/yoga-shepton-mallet/yin-yoga") },
    { path: "/private-yoga-shepton-mallet", element: createRedirect("/areas/yoga-shepton-mallet/private-yoga") },
  ],

  // Legal pages
  legal: [
    { path: "/terms", element: TermsPage },
    { path: "/privacy-policy", element: PrivacyPolicyPage },
    { path: "/cookie-policy", element: CookiePolicyPage },
    { path: "/remote-sessions", element: RemoteSessionsPage },
  ],
};

// Helper to get all routes as a flat array (canonical routes only)
export const getAllRoutes = () => {
  return [
    ...routes.core,
    ...routes.coreRedirects,
    ...routes.yogaClassesChildren,
    ...routes.yogaClassesRedirects,
    ...routes.treatmentsChildren,
    ...routes.treatmentsRedirects,
    ...routes.womensCirclesChildren,
    ...routes.womensCirclesRedirects,
    ...routes.retreatsChildren,
    ...routes.retreatsRedirects,
    ...routes.locations,
    ...routes.locationsRedirects,
    ...routes.serviceLocations,
    ...routes.serviceLocationsRedirects,
    ...routes.miscRedirects,
    ...routes.legal,
  ];
};

// Helper to get only canonical (non-redirect) routes for sitemap
export const getCanonicalRoutes = () => {
  return [
    ...routes.core,
    ...routes.yogaClassesChildren,
    ...routes.treatmentsChildren,
    ...routes.womensCirclesChildren,
    ...routes.retreatsChildren,
    ...routes.locations,
    ...routes.serviceLocations,
    ...routes.legal,
  ];
};

export default routes;
